<template>
  <div class="additional-insured">
    <validation-observer
      v-slot="additionalInsuredFormObserver"
      slim
    >
      <form-section-highlight
        id="additional-insured-edit"
        :error="formObserver.validated && !additionalInsuredFormObserver.valid"
      >
        <!--  ADDITIONAL TITLE  -->
        <component
          :is="'question-base-radio-list'"
          question-id="additional-insured_title"
          product-name="home"
          step-id="additional-insured"
          step-type="multiple"
          :step-index="1"
          :custom-fields="{ field: 'title' }"
          title="What is their title?"
          :help-text="{
            value: 'If their title is not in the list, please select the most appropriate for them'
          }"
          :options-config=" {
            options: constants.TITLES,
          }"
          :validation-rules=" {
            field: {
              required: true
            }
          }"
          :validation-errors=" {
            field: {
              required: 'Please select a title.'
            }
          }"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
        />

        <!--  ADDITIONAL FIRSTNAME, SURNAME  -->
        <component
          :is="'question-name'"
          question-id="additional-insured_name"
          product-name="home"
          step-id="additional-insured"
          step-type="multiple"
          :step-index="1"
          subtitle=" "
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
        />

        <!--  ADDITIONAL DATE OF BIRTH  -->
        <component
          :is="'question-date-of-birth'"
          question-id="additional-insured_date-of-birth"
          product-name="home"
          step-id="additional-insured"
          step-type="multiple"
          :step-index="1"
          class="date-of-birth"
          :help-text="false"
          :form-observer="additionalInsuredFormObserver"
          :form-data="formData"
          :columns-alignment="{
            day: {
              sm: '3',
              cols: '12'
            },
            month: {
              sm: '5',
              cols: '12'
            },
            year: {
              sm: '4',
              cols: '12'
            }
          }"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
        />

        <!--  ADDITIONAL MARTIAL STATUS  -->
        <component
          :is="'question-marital-status'"
          question-id="additional-insured_marital-status"
          product-name="home"
          class="gap-fix"
          step-id="additional-insured"
          step-type="multiple"
          :validation-errors=" {
            maritalStatus: {
              required: 'Please confirm their relationship status.'
            }
          }"
          :step-index="1"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
        />

        <!--  ADDITIONAL RELATIONSHIP TO PROPOSER STATUS  -->
        <validation-observer
          slim
        >
          <form-row
            id="additional-insured_relationship-proposer"
            question="What is their relationship to the proposer?"
          >
            <validation-provider
              v-slot="{ failed }"
              vid="additional-insured_relationship-proposer"
              name="Additional Relationship Proposer"
              :rules="{ is_not: { other: null } }"
              slim
            >
              <radio
                v-model="relationshipProposerModel"
                name="additional-persons-relationship-status"
                column
                wide
                data-test="insured_additional_relationship_proposer"
                return-object
                :items="relationshipProposerModelOptions"
                :error="failed"
                :error-messages="failed ? ['Please confirm their relationship to proposer'] : []"
              />
            </validation-provider>
            <validation-provider
              v-if="isRelationshipProposerOther"
              v-slot="{ errors, failed }"
              vid="insured_additional-insured_relationship-proposer"
              name="Additional Relationship Proposer Other"
              rules="listOptionRequired"
              class="mt-4"
              tag="div"
            >
              <qz-select
                v-model="additionalInsured.relationshipProposer"
                :items="relationshipProposerOtherOptions"
                :error="failed"
                data-test="insured_additional_relationship_proposer_other"
                placeholder="Please select appropriate relationship"
                :error-messages="errors"
              />
            </validation-provider>
          </form-row>
        </validation-observer>

        <!--  ADDITIONAL EMPLOYMENT  -->
        <component
          :is="'question-employment-status'"
          question-id="additional-insured_employment-status"
          product-name="home"
          class="gap-fix"
          step-id="additional-insured"
          step-type="multiple"
          :step-index="1"
          :validation-errors="{
            employmentStatus: {
              is_not: 'Please select their employment status.'
            }
          }"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
        />

        <!--  ADDITIONAL OCCUPATION  -->
        <component
          :is="'question-occupation'"
          question-id="additional-insured_occupation-list"
          product-name="home"
          step-id="additional-insured"
          step-type="multiple"
          subtitle=" "
          :step-index="1"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
          @toast="$emit('toast', $event)"
        />

        <!--  ADDITIONAL BUSINESS  -->
        <component
          :is="'question-business'"
          question-id="additional-insured_business-list"
          product-name="home"
          step-id="additional-insured"
          step-type="multiple"
          :step-index="1"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
          @toast="$emit('toast', $event)"
        />

        <!--  ADDITIONAL JOB  -->
        <component
          :is="'question-another-job'"
          question-id="additional-insured_another-job"
          product-name="home"
          step-id="additional-insured"
          title="Do they have a part-time occupation?"
          title-occupation="What do they do for a living?"
          title-business="What's their employer's business type?"
          subtitle-occupation=" "
          step-type="multiple"
          :step-index="1"
          :optional="true"
          :form-observer="formObserver"
          :form-data="formData"
          :step-data="additionalInsured"
          @input="setAdditionalInsuredData"
          @toast="$emit('toast', $event)"
        />

        <div class="row no-gutters justify-end mt-4">
          <qz-btn
            depressed
            x-large
            dark
            rounded
            color="navy-light"
            data-test="save_details_additional_btn"
            @click="saveAdditionalInsured(additionalInsuredFormObserver)"
          >
            Save details
            <v-icon right>
              mdi-content-save
            </v-icon>
          </qz-btn>
          <qz-btn
            color="primary"
            text
            rounded
            class="ml-2"
            x-large
            data-test="cancel_additional_btn"
            @click.prevent="clearAdditionalInsured"
          >
            Cancel
          </qz-btn>
        </div>
      </form-section-highlight>
    </validation-observer>

    <!-- HIDDEN FIELD TO CHECK EDITOR NOT OPENED -->
    <validation-provider
      v-slot="{ failed }"
      name="Form Open"
      vid="insured_form-open"
      :rules="{ is_not: { other: true } }"
    >
      <input
        :value="formOpen"
        type="hidden"
      >
      <div
        v-if="failed && formObserver.validated"
        class="error--text text-body-1 mt-4"
      >
        You have unsaved details. Please save or cancel these details before continuing.
      </div>
    </validation-provider>
  </div>
</template>

<script>
import getStepOptions from '@/helpers/home/getStepOptions';

import {
  isNull,
} from '@/helpers/products/home';

import {
  cloneDeep,
} from 'lodash';

export default {
  name: 'form-additional-insured',
  inject: ['constants'],
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    insured: {
      type: Object,
      required: true,
    },
    mainInsured: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data () {
    return {
      formOpen: false,

      additionalInsured: null,
    };
  },
  mounted () {
    this.formOpen = true;
    this.$scrollTo('#additional-insured_title');
  },
  computed: {
    // relationship proposer status
    relationshipProposerModel: {
      get () {
        if (isNull(this.additionalInsured.relationshipProposer)) return null;
        return this.constants.INSURED_ADDITIONAL_RELATIONSHIP_PROPOSER_FAVORITES.includes(this.additionalInsured.relationshipProposer) ? this.additionalInsured.relationshipProposer : true;
      },
      set (val) {
        this.additionalInsured.relationshipProposer = val.logical ? val.logicalValue : val.value;
      },
    },
    isRelationshipProposerOther () {
      return !isNull(this.relationshipProposerModel) && !this.options.INSURED_ADDITIONAL_RELATIONSHIP_PROPOSER_FAVORITES.includes(this.relationshipProposerModel);
    },
    isRelationshipProposerOptionsIncludesSelectedValue () {
      const options = [...this.relationshipProposerModelOptions,
        ...this.relationshipProposerOtherOptions]
        .map(option => option.value)
        .filter(option => typeof option !== 'boolean');

      return options.includes(this.additionalInsured.relationshipProposer);
    },

    relationshipProposerModelOptions () {
      return this.options.relationshipProposerItems.filter(({ value }) => this.validateRelationshipItem(value));
    },
    relationshipProposerOtherOptions () {
      return this.options.relationshipProposerItemsOther.filter(({ value }) => this.validateRelationshipItem(value));
    },
  },
  created () {
    this.initForm();
  },
  methods: {
    initForm () {
      this.additionalInsured = this.toggleAdditionalObjectKeys(cloneDeep(this.insured));

      this.$watch('additionalInsured.maritalStatus', () => {
        if (!this.isRelationshipProposerOptionsIncludesSelectedValue) {
          this.additionalInsured.relationshipProposer = null;
        }
      }, {
        immediate: true,
      });

      this.$watch('mainInsured.maritalStatus', () => {
        if (!this.isRelationshipProposerOptionsIncludesSelectedValue) {
          this.additionalInsured.relationshipProposer = null;
        }
      }, {
        immediate: true,
      });
    },

    // toggle 'additional' prefix in additionalInsured object keys
    toggleAdditionalObjectKeys (obj, addAdditionalPrefix = false) {
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = addAdditionalPrefix
          ? `additional${this.toggleFirstLetterCase(key)}`
          : this.toggleFirstLetterCase(key.replace('additional', ''), true);
        acc[newKey] = obj[key];
        return acc;
      }, {});
    },
    toggleFirstLetterCase (string, toLower = false) {
      const firstLetter = toLower ? string[0].toLowerCase() : string[0].toUpperCase();
      return firstLetter + string.slice(1);
    },

    validateRelationshipItem (value) {
      // Can't be Spouse or Civil Partner if main insured is not married and not in civil partnership
      if (['H', 'W', 'P'].includes(value) && !['M', 'V'].includes(this.mainInsured.maritalStatus)) {
        return false;
      }

      // Can't be Spouse or Civil Partner if is not married or not in civil partnership
      if (['H', 'W', 'P'].includes(value) && !['M', 'V'].includes(this.additionalInsured.maritalStatus)) {
        return false;
      }

      return true;
    },

    setAdditionalInsuredData (questionData) {
      this.additionalInsured = { ...this.additionalInsured, ...questionData };
    },
    async saveAdditionalInsured (observer) {
      const isValid = await observer.validate();

      if (!isValid) {
        let failedField = Object.values(observer.fields).find(field => field.invalid).id;

        // Indicate full row if day or month or year is invalid
        if (failedField.includes('date-of-birth')) {
          failedField = 'additional-insured_date-of-birth';
        }

        // Indicate if first name or surname is invalid
        if (failedField.includes('name')) {
          failedField = 'additional-insured_name';
        }

        // Indicate part time job is invalid
        if (failedField.includes('-pt')) {
          failedField = 'additional-insured_another-job';
        }

        // Indicate title is invalid
        if (failedField.includes('title')) {
          failedField = 'additional-insured_title';
        }

        this.$scrollTo(`#${failedField}`);
        return;
      }

      this.$emit('save', this.toggleAdditionalObjectKeys(this.additionalInsured, true));
    },
    clearAdditionalInsured () {
      this.$emit('clear');
    },
  },
  setup () {
    return {
      options: getStepOptions(3),
    };
  },
};
</script>

<style lang='scss'>
.additional-insured {
  margin-top: 48px;

  .form-section:not(:first-child),
  .form-row:not(:first-child) {
    margin-top: 48px;

    &::before {
      content: none;
    }
  }

  .gap-fix .form-row:not(:first-child) {
    margin-top: var(--gap, 24px);
  }

  .form-section__container {
    padding: 0;
  }

  .form-section__container-holder {
    padding-right: 0;
  }

  .form-section__validity {
    display: none;
  }

  .form-section-label {
    font-size: var(--size, 18px);
  }

  .date-of-birth {
    .form-row.columns {
      @media screen and (max-width: 1199px) {
        flex-direction: column;
      }
    }

    .form-col {
      @media screen and (max-width: 1199px) {
        width: 100% !important;

        .form-row__label {
          margin-bottom: 12px;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.date-of-birth::v-deep {
  .form-col {
    width: 100%;

    .form-row-title {
      margin-bottom: 12px;
    }
  }

  &.is-failed .form-section__container::before {
    display: none;
  }
}
</style>
